/**
 * Given a URL or path, this strips any marketing parameters.
 *
 * This is useful for cleaning up URLs before sending them to analytics or
 * for other purposes where we don't want to include marketing parameters.
 *
 * Marketing query params start with `utm_`, an industry standard
 * notation for tracking parameters.
 *
 * @param url The URL to strip marketing parameters from.
 * @returns The URL with marketing parameters stripped. This is the same
 * instance as the input URL.
 */
export const stripMarketingParams = (url: URL): URL => {
    // We cannot use `url.searchParams.delete` inside a
    // `url.searchParams.forEach` or while looping over url.searchParams.keys()
    // because those approaches cause us to skip some parameters as their
    // implementations don't expect the underlying collection to change
    // while iterating over it. This appears to be a quirk of the
    // URLSearchParams implementation.
    // So first, copy the keys.
    const keys = Array.from(url.searchParams.keys());

    // Now we can safely iterate them and modify the params.
    for (const key of keys) {
        if (key.startsWith("utm_")) {
            url.searchParams.delete(key);
        }
    }
    return url;
};
